import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { AmenitySessionOut } from '@/api/amenities';

interface Props {
  session: AmenitySessionOut;
}

/** MM:SS a partir de segundos (nunca negativo). */
function fmt(totalSec: number): string {
  const s = Math.max(0, totalSec);
  const mm = String(Math.floor(s / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}

/**
 * Banner de aspirador em andamento — gêmeo do `ActiveSessionAlert` da recarga
 * (mesmo navy, mesmo arco, mesma pílula "Ver") e renderizado SEMPRE abaixo
 * dele quando os dois estão rodando.
 *
 * O contador roda localmente a cada 1s a partir de `expected_end_at`: o
 * `seconds_remaining` da API só mudaria a cada poll (10s) e um relógio pulando
 * de 10 em 10 na home fica pior que não ter relógio.
 */
export function ActiveVacuumAlert({ session }: Props) {
  const endsAt = session.expected_end_at
    ? new Date(session.expected_end_at).getTime()
    : null;

  const remainingNow = () =>
    endsAt !== null
      ? Math.round((endsAt - Date.now()) / 1000)
      : session.seconds_remaining;

  const [remaining, setRemaining] = useState(remainingNow);

  useEffect(() => {
    setRemaining(remainingNow());
    const id = window.setInterval(() => setRemaining(remainingNow()), 1_000);
    return () => window.clearInterval(id);
    // `endsAt` muda quando a sessão do poll é outra; o resto é derivado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endsAt, session.seconds_remaining]);

  return (
    <Link
      to={`/amenities/sessions/${session.id}`}
      className="relative flex items-center justify-between overflow-hidden rounded-3xl bg-bow-royal p-5 text-bow-cream shadow-deep"
    >
      <span
        aria-hidden
        className="pointer-events-none absolute -right-16 -top-10 h-44 w-44 rounded-full opacity-50"
        style={{
          background:
            'conic-gradient(from 220deg at 50% 100%, transparent, rgba(0,192,114,0.5) 30%, rgba(0,187,222,0.5) 50%, rgba(131,69,186,0.5) 70%, transparent 90%)',
          filter: 'blur(20px)'
        }}
      />
      <div className="relative flex flex-col gap-1.5">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-bow-cream/65">
          <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-bow-green" />
          Aspirador em andamento
        </span>
        <span className="text-lg font-semibold tracking-tighter tabular-nums">
          {fmt(remaining)} restantes
        </span>
      </div>
      <span className="relative inline-flex h-10 items-center justify-center rounded-full bg-bow-cream px-4 text-sm font-semibold text-bow-royal">
        Ver
      </span>
    </Link>
  );
}
